import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import {useState} from "react";

import {useOnMountEffect, View} from "@khanacademy/wonder-blocks-core";
import {TextField} from "@khanacademy/wonder-blocks-form";
import IconButton from "@khanacademy/wonder-blocks-icon-button";
import {Spring} from "@khanacademy/wonder-blocks-layout";
import {border, semanticColor, sizing} from "@khanacademy/wonder-blocks-tokens";
import {LabelLarge, LabelSmall} from "@khanacademy/wonder-blocks-typography";
import {useGuidePreferences, useRunGuideComponent} from "@khan/guide-core";
import {ActionBubbles, GuideIcon32, LoadingDots} from "@khan/guide-ui";

import {type Suggestion} from "./suggestions.ts";
import type {JsonPath} from "./types.ts";
import {useCoeditingState} from "./use-coediting-state.ts";
import {getJsonDataAtPath} from "./utils.ts";

import xIcon from "@phosphor-icons/core/bold/x-bold.svg";
import paperAirplaneIcon from "@phosphor-icons/core/fill/paper-plane-tilt-fill.svg";

interface Props {
    path: JsonPath;
    suggestion: Suggestion;
    updateSuggestion: (suggestion: Suggestion | null) => void;
    handleSuggestionClick: (suggestion: Suggestion) => void;
}

/**
 * This component renders the input that can appear within the "Ask Khanmigo"
 * popup when your selected suggestion requires additional information. For
 * example, a user may select some text and click on "Make changes to this".
 * At that point, the dropdown would change to this text field so the user
 * can provide details on the kinds of changes they want.
 */
export default function AskKhanmigoTextInput(props: Props) {
    const {path, suggestion, handleSuggestionClick, updateSuggestion} = props;

    const {data} = useCoeditingState();

    const [loading, setLoading] = useState(true);
    const [dynamicSuggestions, setDynamicSuggestions] = useState<string[]>([]);

    const {preferences} = useGuidePreferences();
    const getSuggestions = useRunGuideComponent("CoeditingJSONSuggestions");

    const inputText = suggestion?.additionalText ?? "";

    useOnMountEffect(() => {
        setLoading(true);

        const dataToReplace = getJsonDataAtPath(data, path);

        getSuggestions({
            path,
            fullData: data,
            dataToReplace,
            instruction: suggestion.text(),
            guideLanguageCode: preferences.guideLanguageCode,
        }).then((response) => {
            setLoading(false);

            if (response.ok) {
                setDynamicSuggestions(response.output.suggestions);
            }
        });
    });

    return (
        <View
            style={styles.wrapper}
            role="dialog"
            aria-label={t`Ask Khanmigo: ${suggestion.text()}`}
        >
            <View style={styles.topBar}>
                <GuideIcon32 id="khanmigo-icon" style={styles.khanmigoIcon} />
                <LabelLarge style={styles.editingTitle}>
                    {suggestion.text()}
                </LabelLarge>
                <Spring />
                <View style={styles.caretIcon}>
                    <IconButton
                        size="xsmall"
                        icon={xIcon}
                        kind="tertiary"
                        onClick={() => {
                            updateSuggestion(null);
                        }}
                        aria-label={t`Close`}
                    />
                </View>
            </View>
            <View style={styles.textInputContainer}>
                <TextField
                    id="toolbar-chat-input"
                    placeholder={t`Give me more detail`}
                    aria-label={t`Give me more detail`}
                    value={inputText}
                    onChange={(event) => {
                        updateSuggestion({
                            ...suggestion,
                            additionalText: event,
                        });
                    }}
                    disabled={false} // Enable typing in the TextField
                    autoFocus={true}
                    autoComplete="off"
                    onKeyDown={(event) => {
                        event.stopPropagation();

                        if (event.key === "Enter") {
                            event.preventDefault();
                            updateSuggestion(null);
                            handleSuggestionClick({
                                ...suggestion,
                                additionalText: inputText,
                            });
                        } else if (event.key === "Escape") {
                            event.preventDefault();
                            updateSuggestion(null);
                        }
                    }}
                />
                <IconButton
                    onClick={() => {
                        handleSuggestionClick({
                            ...suggestion,
                            additionalText: inputText,
                        });
                    }}
                    aria-label={t`Send`}
                    disabled={inputText.length === 0}
                    icon={paperAirplaneIcon}
                    kind="tertiary"
                    size="small"
                    style={styles.sendIconButton}
                />
            </View>
            {loading && (
                <View style={styles.loading}>
                    <LabelSmall>{t`Loading`}</LabelSmall>
                    <LoadingDots
                        color={semanticColor.status.neutral.foreground}
                        size="small"
                    />
                </View>
            )}
            {!loading && dynamicSuggestions.length && (
                <ActionBubbles
                    style={styles.bubbles}
                    onActionSelected={(bubble) => {
                        handleSuggestionClick({
                            ...suggestion,
                            additionalText: bubble.label,
                        });
                    }}
                    actionBubbles={dynamicSuggestions.map((s) => ({
                        label: s,
                        command: s,
                        actions: [],
                    }))}
                />
            )}
        </View>
    );
}

const styles = StyleSheet.create({
    wrapper: {
        width: 400,
        padding: sizing.size_060,
        position: "absolute",
        border: `1px solid ${semanticColor.core.border.neutral.subtle}`,
        background: semanticColor.surface.primary,
        borderRadius: border.radius.radius_040,
        boxShadow: `0 8px 8px 0 rgba(0,0,0,0.08),
            0 4px 4px rgba(0,0,0,0.08)`,
        insetInlineEnd: 0,
        top: `calc(-1 * ${sizing.size_080})`,
        transform: "translateY(-100%)",
    },
    topBar: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "start",
        paddingInlineStart: sizing.size_020,
        paddingInlineEnd: sizing.size_020,
        paddingBlockEnd: sizing.size_080,
    },
    khanmigoIcon: {
        marginInlineStart: 0,
        marginInlineEnd: sizing.size_080,
        width: sizing.size_320,
        height: sizing.size_320,
    },
    caretIcon: {
        paddingInlineStart: sizing.size_120,
    },
    editingTitle: {
        fontWeight: "normal",
    },
    textInputContainer: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        padding: sizing.size_080,
        paddingBlockStart: 0,
    },
    sendIconButton: {
        alignSelf: "center",
        margin: 0,
        marginInlineStart: sizing.size_080,
        flexShrink: 0,
    },
    loading: {
        flexDirection: "row",
        justifyContent: "center",
        alignItems: "center",
        color: semanticColor.status.neutral.foreground,
        padding: sizing.size_080,
        gap: sizing.size_040,
    },
    bubbles: {
        gap: sizing.size_080,
        padding: sizing.size_080,
    },
});
